import {
  RefObject,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { revealChatMenu } from './ChatSearch.motion';
import { tChatSearchContext } from './ChatSearch.types';

export type tChatSearchPresence = {
  /** The bar is on screen: open, or playing its way out after "Cerrar". */
  barMounted: boolean;
  /** The bar is playing its way out; the search itself is already closed. */
  leaving: boolean;
  /** ChatMenu's controls are out of sight (transparent, still laid out, so
   *  the bar can measure the magnifier). */
  menuHidden: boolean;
  /** ChatMenu is out of reach. Only while the search is open: on close the
   *  focus goes back to the magnifier at once, even while the bar leaves. */
  menuInert: boolean;
  menuRef: RefObject<HTMLDivElement>;
  /** The bar's way out has played. */
  onBarLeft: () => void;
};

/**
 * What the ChatMenu row shows around the in-chat search (SRE-3328): ChatMenu,
 * the bar, or the bar leaving over a still-hidden ChatMenu.
 *
 * Opening hides ChatMenu's controls on the next frame, with no transition.
 * Closing with "Cerrar" keeps the bar mounted while it goes back into the
 * magnifier, and then brings the controls back with the DS `reveal` enter.
 * Closing from the keyboard (Esc), or for any other reason, swaps them at once.
 *
 * @param chatSearch - The panel's search, or null with the feature off.
 */
export default function useChatSearchPresence(
  chatSearch: tChatSearchContext | null,
): tChatSearchPresence {
  const isOpen = Boolean(chatSearch?.isOpen);
  const animated = chatSearch?.motion === 'animate';
  const [wasOpen, setWasOpen] = useState(isOpen);
  const [leaving, setLeaving] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const revealRef = useRef(false);

  // Decided during render, not in an effect, so the bar is never unmounted
  // for a frame between "closed" and "leaving".
  if (wasOpen !== isOpen) {
    setWasOpen(isOpen);
    setLeaving(!isOpen && animated);
  }

  const onBarLeft = useCallback(() => {
    revealRef.current = true;
    setLeaving(false);
  }, []);

  useLayoutEffect(() => {
    if (isOpen) revealRef.current = false;
    if (!revealRef.current || isOpen || leaving) return;

    revealRef.current = false;
    revealChatMenu(menuRef.current);
  }, [isOpen, leaving]);

  return {
    barMounted: isOpen || leaving,
    leaving,
    menuHidden: isOpen || leaving,
    menuInert: isOpen,
    menuRef,
    onBarLeft,
  };
}
